import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Tab } from "@headlessui/react";
import { useNavigate } from "react-router-dom";

import { Navbar } from "../components/layouts/Navbar";
import { PlannerSidebar } from "../components/layouts/PlannerSidebar";
import { Section, TwoColumnGrid, FormField, ClienteData } from "../components/form/FormFields";

// --- Props esperadas pelo formulário ---
interface ClientesFormProps {
  id?: string;                   // UUID opcional
  client: ClienteData;           // dados do cliente
  isLoading: boolean;            // controla loading
  onSubmit: (data: ClienteData) => void;
}

// --- Seções do formulário ---
const sections = [
  "Dados Pessoais",
  "Documentos",
  "Endereço",
  "Dados Bancários",
  "Cartão de Crédito",
  "Outros",
] as const;

type SectionKeys = (typeof sections)[number];

// --- Campos do formulário (sem campos mascarados) ---
const formFields: Record<SectionKeys, any[]> = {
  "Dados Pessoais": [
    { type: "text", name: "nome", label: "Nome", required: true },
    { type: "text", name: "nome_preferencial", label: "Nome Preferencial" },
    { type: "text", name: "cpf_cnpj", label: "CPF/CNPJ", required: true },
    { type: "text", name: "telefone", label: "Telefone" },
    { type: "text", name: "email", label: "E-mail" },
    { type: "text", name: "email_secundario", label: "E-mail Secundário" },
    { type: "date", name: "nascimento_abertura", label: "Nascimento/Abertura" },
    { type: "text", name: "sexo", label: "Sexo" },
    { type: "text", name: "estado_civil", label: "Estado Civil" },
  ],
  "Documentos": [
    { type: "text", name: "rg", label: "RG" },
    { type: "text", name: "tipo_documento", label: "Tipo de Documento" },
    { type: "date", name: "data_expedicao", label: "Data de Expedição" },
    { type: "text", name: "orgao_expedidor", label: "Órgão Expedidor" },
    { type: "text", name: "naturalidade", label: "Naturalidade" },
    { type: "text", name: "cnh", label: "CNH" },
    { type: "date", name: "venc_cnh", label: "Vencimento CNH" },
  ],
  "Endereço": [],
  "Dados Bancários": [],
  "Cartão de Crédito": [],
  "Outros": [],
};

// --- Função auxiliar para aplicar classes condicionalmente ---
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

// --- Componente principal ---
export default function ClientesForm({ id, client, isLoading, onSubmit }: ClientesFormProps) {
  const navigate = useNavigate();

  // --- Configuração do react-hook-form ---
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ClienteData>({
    defaultValues: client, // valores iniciais do formulário
  });

  // 🔑 Quando `client` muda (fetch finalizado), atualiza o form
  useEffect(() => {
    reset(client);
  }, [client, reset]);

  // --- Renderiza os campos de cada seção ---
  const renderFields = (section: SectionKeys) =>
    formFields[section].map((field) => (
      <FormField
        key={field.name}
        name={field.name}
        label={field.label}
        type={field.type}
        required={field.required}
        control={control}
        errors={errors}
      />
    ));

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex p-6 gap-3">
        {/* Conteúdo principal */}
        <div className="flex-1">
          <h1 className="text-2xl font-bold mb-2">
            {id ? "Editar Cliente" : "Novo Cliente"}
          </h1>

          {isLoading ? (
            <p className="text-gray-500">Carregando...</p>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-8 max-w-6xl"
            >
              <Tab.Group>
                {/* Lista de abas */}
                <Tab.List className="flex space-x-2 border-b mb-2">
                  {sections.map((section) => (
                    <Tab
                      key={section}
                      className={({ selected }) =>
                        classNames(
                          "px-2 py-1 text-xs font-medium",
                          selected
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-black"
                        )
                      }
                    >
                      {section}
                    </Tab>
                  ))}
                </Tab.List>

                {/* Painéis de conteúdo */}
                <Tab.Panels className="space-y-8">
                  {sections.map((section) => (
                    <Tab.Panel key={section}>
                      <Section title={section}>
                        {formFields[section].length > 0 ? (
                          <TwoColumnGrid>{renderFields(section)}</TwoColumnGrid>
                        ) : (
                          <p className="text-gray-500">
                            Nenhum campo configurado para esta seção.
                          </p>
                        )}
                      </Section>
                    </Tab.Panel>
                  ))}
                </Tab.Panels>
              </Tab.Group>

              {/* Botões Salvar / Cancelar */}
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Salvar
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/clientes")}
                  className="px-2 py-1 bg-gray-300 text-black rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Sidebar do planner */}
        <div className="hidden lg:block">
          <PlannerSidebar />
        </div>
      </div>
    </div>
  );
}
